import { useState, useEffect, useCallback } from 'react';
import { pushApi } from '../api/push';

// =============================================
// Hilfsfunktion: VAPID Base64 → Uint8Array
// =============================================
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// =============================================
// usePushNotifications Hook
// =============================================
export function usePushNotifications() {
  const isSupported =
    typeof window !== 'undefined' &&
    'Notification' in window &&
    'serviceWorker' in navigator &&
    'PushManager' in window;

  const [permission, setPermission] = useState<NotificationPermission>(
    isSupported ? Notification.permission : 'denied'
  );
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Subscription-Status beim Mount prüfen
  useEffect(() => {
    if (!isSupported) return;

    const checkSubscription = async () => {
      try {
        const registration = await navigator.serviceWorker.ready;
        const existing = await registration.pushManager.getSubscription();
        setIsSubscribed(!!existing);
      } catch {
        setIsSubscribed(false);
      }
    };

    checkSubscription();
  }, [isSupported]);

  // Subscribe
  const subscribe = useCallback(async () => {
    if (!isSupported) return;

    setIsLoading(true);
    try {
      // Berechtigung anfragen
      const result = await Notification.requestPermission();
      setPermission(result);

      if (result !== 'granted') {
        return;
      }

      // VAPID Public Key vom Backend holen
      const vapidPublicKey = await pushApi.getVapidKey();
      const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);

      // Service Worker Registration holen
      const registration = await navigator.serviceWorker.ready;

      // Subscription erstellen
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: applicationServerKey as BufferSource,
      });

      // An Backend senden
      await pushApi.subscribe(subscription.toJSON());
      setIsSubscribed(true);
    } catch (error) {
      console.error('[Push] Fehler beim Abonnieren:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [isSupported]);

  // Unsubscribe
  const unsubscribe = useCallback(async () => {
    if (!isSupported) return;

    setIsLoading(true);
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();

      if (subscription) {
        // Backend informieren
        await pushApi.unsubscribe(subscription.endpoint);
        // Vom Browser entfernen
        await subscription.unsubscribe();
      }

      setIsSubscribed(false);
    } catch (error) {
      console.error('[Push] Fehler beim Abmelden:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, [isSupported]);

  // Test-Benachrichtigung
  const sendTest = useCallback(async () => {
    await pushApi.test();
  }, []);

  return {
    isSupported,
    isSubscribed,
    permission,
    isLoading,
    subscribe,
    unsubscribe,
    sendTest,
  };
}
